import { Table, useAlert, useFilters } from "@wit/mpesa-ui-components";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { IStoreInterface } from "../../../../configs/store.config";
import TablePagination from "../../../../shared/components/table-pagination/table-pagination-component";
import { IGatewayLog } from "../../../../shared/models/gateway-log.model";
import { LoadingText } from "../../../../shared/shared.styled";
import SupportApi from "../../support.api";
import { GatewayLogStatusEnum, IGwReportLogsRequest } from "../../support.interfaces";
import { SupportActions } from "../../support.store";
import { SupportUtils } from "../../support.utils";
import GatewayLogsFilters, { GatewayFilterKeys } from "./gateway-logs-filters.component";
import RequestDrawer from "./request-drawer.component";

/**
 * Gateway logs
 * @returns
 */
const GatewayLogsTab = () => {
  // Hooks init
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const {
    filters,
    updateFilter,
    isFilterActive,
    clearFilter,
    resetFilters,
    getFilterValue,
    getFiltersQueryString,
    updateMultipleFilters,
  } = useFilters();

  // Local state
  const firstUpdate = React.useRef(true);
  const [isFetchingLogs, setIsFetchingLogs] = React.useState(false);
  const [drawerIsOpen, setDrawerIsOpen] = React.useState(false);
  const [selectedLog, setSelectedLog] = React.useState<IGatewayLog | undefined>();
  const [page, setActivePage] = React.useState(1);
  const pageSize = 20;
  const [requestParams, setRequestParams] = React.useState<IGwReportLogsRequest>({
    page: 1,
    pageSize: 20,
  });
  const [requestParamsTemp, setRequestParamsTemp] = React.useState<IGwReportLogsRequest>({
    page: 1,
    pageSize: 20,
  });

  // Redux state
  const { gatewayLogs, gatewayLogsCount } = useSelector((state: IStoreInterface) => state.supportReducer);

  /**
   * Show log drawer
   * @param gatewayLog
   */
  const showLogDrawer = (gatewayLog: IGatewayLog) => {
    setSelectedLog(gatewayLog);
    setDrawerIsOpen(true);
  };

  /*
   * Get gateway logs on page change
   * */
  React.useEffect(() => {
    setRequestParams({ ...requestParams, page: page });
  }, [dispatch, page]);

  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    getGatewayLogs();
  }, [requestParams]);

  /*
   * Updates the request state each time the filters are changed
   * */
  React.useEffect(() => {
    let request: IGwReportLogsRequest = { page, pageSize };
    const dateFilter = getDateFilter();
    if (!dateFilter) {
      const timeFilter = getTimeFilter();
      request = { ...request, ...formatTimeFilter(timeFilter) };
    } else {
      request = { ...request, ...dateFilter };
    }
    request = {
      ...request,
      ...getGatewayFilterKey(GatewayFilterKeys.REQUEST_ID),
      ...getGatewayFilterKey(GatewayFilterKeys.REQUEST_CONTENT),
      ...getGatewayFilterKey(GatewayFilterKeys.ASYNC_RESPONSE_CONTENT),
      ...getGatewayFilterKey(GatewayFilterKeys.SYNC_RESPONSE_CONTENT),
      ...getGatewayFilterKey(GatewayFilterKeys.OPERATION),
      ...getStatusFilter(),
    };
    setRequestParamsTemp(request);
  }, [filters]);

  /**
   * Refresh results handler
   * */
  const refreshResults = () => {
    setActivePage(1);
    setRequestParams({ ...requestParamsTemp, page: 1 });
  };

  /**
   * Returns the status filter
   * */
  const getStatusFilter = () => {
    if (filters.get(GatewayFilterKeys.STATUS)) {
      const status = (filters.get(GatewayFilterKeys.STATUS) as string[])[0];
      // @ts-ignore
      return { requestStatus: GatewayLogStatusEnum[status] };
    }
    return null;
  };

  /**
   * Get Gateway filter keys
   * @param filterKey
   * @returns
   */
  const getGatewayFilterKey = (filterKey: GatewayFilterKeys) => {
    if (filters.get(filterKey)) {
      return { [filterKey]: (filters.get(filterKey) as string[])[0] };
    }
    return null;
  };

  /**
   * Format time filter
   * */
  const formatTimeFilter = (timeFilter: { startTime: string[]; endTime: string[] } | null) => {
    if (timeFilter) {
      const startDate = moment()
          .utcOffset(0)
          .hours(Number(timeFilter.startTime[0]))
          .minutes(Number(timeFilter.startTime[1]))
          .seconds(0),
        endDate = moment()
          .utcOffset(0)
          .hours(Number(timeFilter.endTime[0]))
          .minutes(Number(timeFilter.endTime[1]))
          .seconds(0);
      if (endDate.isBefore(startDate)) {
        endDate.add(1, "d");
      }
      return { startDate: startDate.unix(), endDate: endDate.unix() };
    }
    return null;
  };

  /**
   * Returns the time filter object
   * */
  const getTimeFilter = () => {
    if (filters.get(GatewayFilterKeys.TIME_RANGE)) {
      const startTime = (filters.get(GatewayFilterKeys.TIME_RANGE) as string[])[0].split(":");
      const endTime = (filters.get(GatewayFilterKeys.TIME_RANGE) as string[])[1].split(":");
      return { startTime: startTime, endTime: endTime };
    }
    return null;
  };

  /**
   * Returns the date filter object
   * */
  const getDateFilter = () => {
    if (filters.get(GatewayFilterKeys.START_DATE)) {
      let startDate,
        endDate = null;
      const timeFilter = getTimeFilter();
      if (timeFilter) {
        startDate = moment((filters.get(GatewayFilterKeys.START_DATE) as string[])[0])
          .utcOffset(0)
          .hours(Number(timeFilter.startTime[0]))
          .minutes(Number(timeFilter.startTime[1]))
          .seconds(0);
        endDate = moment((filters.get(GatewayFilterKeys.END_DATE) as string[])[0])
          .utcOffset(0)
          .hours(Number(timeFilter.endTime[0]))
          .minutes(Number(timeFilter.endTime[1]))
          .seconds(59);
      } else {
        startDate = moment((filters.get(GatewayFilterKeys.START_DATE) as string[])[0])
          .utcOffset(0)
          .hours(0)
          .minutes(0)
          .seconds(0);
        endDate = moment((filters.get(GatewayFilterKeys.END_DATE) as string[])[0])
          .utcOffset(0)
          .hours(0)
          .minutes(0)
          .seconds(0)
          .add(1, "d");
      }
      return { startDate: startDate.unix(), endDate: endDate.unix() };
    }
    return null;
  };

  /**
   * Get gateway logs method
   * */
  const getGatewayLogs = () => {
    setIsFetchingLogs(true);
    SupportApi.methods
      .getGatewayLogs(requestParams)
      .finally(() => setIsFetchingLogs(false))
      .then(
        res => {
          const { gwReportLogList, count } = res.data;
          dispatch(SupportActions.Creators.fetchGatewayLogsSuccessAction(gwReportLogList, count));
        },
        () => {
          dispatch(SupportActions.Creators.fetchGatewayLogsErrorAction());
          setAlertProps({
            title: t("pages.support.gatewayLogs.getLogsError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
        },
      );
  };

  /**
   * Handles the pagination change
   * */
  const handlePageChange = (pageNumber: number) => {
    setActivePage(pageNumber);
  };
  return (
    <>
      <GatewayLogsTabContainer>
        <GatewayLogsFilters
          filters={filters}
          isFilterActive={isFilterActive}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          resetFilters={resetFilters}
          getFilterValue={getFilterValue}
          getFiltersQueryString={getFiltersQueryString}
          updateMultipleFilters={updateMultipleFilters}
          gatewayLogsCount={gatewayLogsCount}
          refreshResults={refreshResults}
        />
        {isFetchingLogs ? (
          <LoadingText>{t("commons.loadingResults")}</LoadingText>
        ) : (
          <>
            <Table
              rowClickAction={(idx, log) => showLogDrawer(log)}
              values={gatewayLogs}
              columns={SupportUtils.getGatewayLogsColumns()}
            />
          </>
        )}
        {!isFetchingLogs && gatewayLogsCount ? (
          <TablePagination
            handlePageChange={handlePageChange}
            totalItems={gatewayLogsCount}
            activePage={page}
            pageSize={pageSize}
          />
        ) : null}
      </GatewayLogsTabContainer>
      {drawerIsOpen ? (
        <RequestDrawer
          gwReportLogId={(selectedLog as IGatewayLog).id}
          gwReportLogRequestId={(selectedLog as IGatewayLog).requestId}
          hideDrawer={() => setDrawerIsOpen(false)}
        />
      ) : null}
    </>
  );
};

export default GatewayLogsTab;

const GatewayLogsTabContainer = styled("div")`
  display: flex;
  flex-direction: column;
`;
